import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, LogOut, PackageSearch, Mail, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import { MOON } from "@/content/moon";
import { useLang } from "@/lib/lang";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function ClientDashboard() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data: d }) => {
        setMe(d);
        return api.get("/client/my-requests");
      })
      .then(({ data: d }) => setData(d))
      .catch((e) => {
        if (e.response?.status === 401) navigate("/client");
        else setError(formatApiErrorDetail(e.response?.data?.detail));
      });
  }, [navigate]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/");
  };

  const STATUS_LABELS = {
    fr: { nouvelle: "Nouvelle demande", en_analyse: "En cours d'analyse", recherche_fournisseur: "Recherche fournisseur en cours", devis_recu: "Devis reçu (fournisseur)", devis_envoye: "Devis envoyé au client", acceptee: "Acceptée", commande_en_cours: "Commande en cours", livree: "Livrée ou terminée", annulee: "Annulée", nouveau: "Nouvelle demande", en_cours: "En cours", traite: "Traitée" },
    en: { nouvelle: "New request", en_analyse: "Under analysis", recherche_fournisseur: "Supplier search in progress", devis_recu: "Quote received (supplier)", devis_envoye: "Quote sent to client", acceptee: "Accepted", commande_en_cours: "Order in progress", livree: "Delivered or completed", annulee: "Cancelled", nouveau: "New request", en_cours: "In progress", traite: "Processed" },
  };
  const statusLabel = (s) => (STATUS_LABELS[lang] && STATUS_LABELS[lang][s]) || s;

  return (
    <main className="min-h-screen bg-paper pb-20 pt-[120px] md:pt-[160px]" data-testid="client-dashboard">
      <Seo title={lang === "fr" ? "Mon espace client | Export Monde Prestige Inc." : "My client area | Export Monde Prestige Inc."} description={lang === "fr" ? "Suivi de vos demandes et devis." : "Track your requests and quotes."} path="/client/tableau-de-bord" />
      <div className="mx-auto max-w-[1100px] px-5 md:px-8">
        <Link to="/" className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-steel transition-colors hover:text-electric">
          <ArrowLeft size={15} /> {lang === "fr" ? "Retour au site" : "Back to site"}
        </Link>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-navy-950 md:text-4xl">
              {lang === "fr" ? "Bonjour" : "Hello"}, {me?.name || ""}
            </h1>
            {me?.company && <p className="mt-1 text-sm text-graphite">{me.company}</p>}
          </div>
          <button onClick={logout} data-testid="client-logout-button" className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-xs font-bold text-navy-800 hover:border-red-300 hover:text-red-600">
            <LogOut size={14} /> {lang === "fr" ? "Quitter" : "Sign out"}
          </button>
        </div>

        {error && <p role="alert" className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</p>}
        {!data && !error && <p className="mt-8 text-sm text-steel">{lang === "fr" ? "Chargement…" : "Loading…"}</p>}

        {data && (
          <div className="mt-10 grid gap-8 lg:grid-cols-2">
            <section data-testid="client-equipment-section">
              <h2 className="flex items-center gap-2 font-display text-xl font-bold text-navy-950">
                <PackageSearch size={20} className="text-electric" /> {lang === "fr" ? "Demandes d'équipement (MOON)" : "Equipment requests (MOON)"}
              </h2>
              {data.equipment.length === 0 && (
                <div className="mt-4 rounded-2xl border border-slate-200/90 bg-white p-6 text-sm text-graphite" data-testid="client-equipment-empty">
                  {lang === "fr" ? "Aucune demande pour le moment." : "No requests yet."}{" "}
                  <Link to="/moon/recherche" className="font-bold text-electric underline">{lang === "fr" ? "Trouver un équipement" : "Find equipment"}</Link>
                </div>
              )}
              <div className="mt-4 space-y-3">
                {data.equipment.map((r) => (
                  <div key={r.request_number} className="rounded-2xl border border-slate-200/90 bg-white p-5" data-testid={`client-equipment-row-${r.request_number}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-mono text-xs font-bold tracking-wider text-sky-700">{r.request_number}</p>
                      <span className="rounded-full bg-ice px-3 py-1 text-[11px] font-bold text-sky-700">{statusLabel(r.status)}</span>
                    </div>
                    <p className="mt-2 text-sm font-bold text-navy-950">{r.product}</p>
                    <p className="mt-1 text-[12.5px] text-graphite">
                      {t(MOON.types[r.request_type] || { fr: r.request_type, en: r.request_type })} · {r.quantity || 1}× · {r.city}, {r.province}
                    </p>
                    <p className="mt-1 text-[11px] text-steel">{new Date(r.created_at).toLocaleDateString(lang === "en" ? "en-CA" : "fr-CA")}</p>
                  </div>
                ))}
              </div>
            </section>

            <section data-testid="client-consultations-section">
              <h2 className="flex items-center gap-2 font-display text-xl font-bold text-navy-950">
                <Mail size={20} className="text-electric" /> {lang === "fr" ? "Demandes de consultation" : "Consultation requests"}
              </h2>
              {data.consultations.length === 0 && (
                <div className="mt-4 rounded-2xl border border-slate-200/90 bg-white p-6 text-sm text-graphite" data-testid="client-consultations-empty">
                  {lang === "fr" ? "Aucune consultation pour le moment." : "No consultations yet."}{" "}
                  <Link to="/contact" className="font-bold text-electric underline">{lang === "fr" ? "Demander un devis" : "Request a quote"}</Link>
                </div>
              )}
              <div className="mt-4 space-y-3">
                {data.consultations.map((l) => (
                  <div key={l.id} className="rounded-2xl border border-slate-200/90 bg-white p-5" data-testid={`client-consultation-row-${l.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-bold text-navy-950">{l.service_label}</p>
                      <span className="rounded-full bg-ice px-3 py-1 text-[11px] font-bold text-sky-700">{statusLabel(l.status)}</span>
                    </div>
                    <p className="mt-1 text-[11px] text-steel">{new Date(l.created_at).toLocaleDateString(lang === "en" ? "en-CA" : "fr-CA")}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}

        <p className="mt-12 flex items-start gap-3 rounded-2xl border border-sky-200 bg-ice p-5 text-[12.5px] leading-relaxed text-navy-900">
          <ShieldCheck size={18} className="mt-0.5 shrink-0 text-electric" />
          {lang === "fr"
            ? "Vous ne voyez que les demandes soumises avec le courriel de votre compte. Aucun stock, prix ou délai n'est confirmé automatiquement : chaque mise à jour est validée par notre équipe."
            : "You only see requests submitted with your account's email. No stock, price or lead time is confirmed automatically: every update is verified by our team."}
        </p>
      </div>
    </main>
  );
}
